import React, { useState, useMemo, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Alert,
  Box,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TrussResultsFigure from './TrussResultsFigure';
import TrussForcesFigure from './TrussForcesDiagram';
import BracingLoadPathsView from './BracingLoadPathsView';
import TrussLoadingsView from './TrussLoadingsView';
import { formatGoverningCombination } from './trussLoadingsShared';

function utilBar(util) {
  const u = Math.min(100, Math.max(0, (util || 0) * 100));
  const color = u > 100 ? 'error' : u > 90 ? 'warning' : 'primary';
  return <LinearProgress variant="determinate" value={Math.min(100, u)} color={color} sx={{ height: 8, borderRadius: 1 }} />;
}

function utilPercent(u) {
  const x = Number(u) || 0;
  if (!Number.isFinite(x) || x > 1e6) return '—';
  return `${(x * 100).toFixed(1)}%`;
}

function formatProfile(p) {
  if (!p || (p.name == null && p.type == null)) return '—';
  const t = p.type || '';
  const n = p.name || '';
  return [t, n].filter(Boolean).join(' ');
}

function profilesSummary(sp, geometry) {
  if (!sp || typeof sp !== 'object') return [];
  const rows = [];
  rows.push({ label: 'Bottom chord', value: formatProfile(sp.bottom_chord) });
  const tl = sp.top_chord_left;
  const tr = sp.top_chord_right;
  const tc = sp.top_chord;
  if (formatProfile(tl) !== '—' || formatProfile(tr) !== '—') {
    if (formatProfile(tl) === formatProfile(tr)) {
      rows.push({ label: 'Top chord (left & right)', value: formatProfile(tl) || formatProfile(tc) });
    } else {
      rows.push({ label: 'Top chord (left)', value: formatProfile(tl) || formatProfile(tc) });
      rows.push({ label: 'Top chord (right)', value: formatProfile(tr) || formatProfile(tc) });
    }
  } else {
    rows.push({ label: 'Top chord', value: formatProfile(tc) });
  }
  const hasVerticalMembers =
    Array.isArray(geometry?.members) && geometry.members.some((m) => m.group === 'vertical');
  if (hasVerticalMembers) {
    rows.push({ label: 'Verticals', value: formatProfile(sp.vertical) });
  }
  const dg = sp.diagonal_groups;
  if (Array.isArray(dg) && dg.length) {
    dg.forEach((d) => {
      const idx = d.group_index != null ? d.group_index : rows.length;
      rows.push({ label: `Diagonals (group ${idx})`, value: formatProfile(d) });
    });
  }
  if (sp.column) {
    rows.push({ label: 'Portal column', value: formatProfile(sp.column) });
  }
  return rows;
}

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return (
    <Card variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
      <CardContent sx={{ pt: 2 }}>{children}</CardContent>
    </Card>
  );
}

/** Chord-face gap vs overlap (outer-fibre g model). */
function formatWeldChordGapMm(w) {
  if (w.overlap_gap_kind && w.overlap_gap_abs_mm != null) {
    const v = Number(w.overlap_gap_abs_mm).toFixed(1);
    if (w.overlap_gap_kind === 'gap') return `gap ${v} mm`;
    return `overlap ${v} mm`;
  }
  if (w.gap_m != null) {
    const isOverlap = Number(w.gap_m) < 0;
    const v = (Math.abs(Number(w.gap_m)) * 1000).toFixed(1);
    return isOverlap ? `overlap ${v} mm` : `gap ${v} mm`;
  }
  return '—';
}

function formatLoadVariantLabel(s) {
  if (s == null || s === '') return '—';
  return String(s).replace(/_/g, ' ');
}

const FILLET_STRUCT_MIN_MM = 3;

/** Throat a for display: shop minimum 3 mm shown without triggering a low-throat warning in the UI. */
function displayThroatAMm(w) {
  const a = Number(w.fillet_a_prov_mm);
  if (!Number.isFinite(a)) return { text: '—', clamped: false };
  if (a < FILLET_STRUCT_MIN_MM - 1e-9) return { text: FILLET_STRUCT_MIN_MM.toFixed(0), clamped: true };
  return { text: a.toFixed(2), clamped: false };
}

/** L_eff in m; JSON null must not become 0 (Number(null) === 0 in JS). */
function _finiteLeffM(raw) {
  if (raw == null) return NaN;
  const n = Number(raw);
  return Number.isFinite(n) ? n : NaN;
}

function formatWeldLeffColumnM(w) {
  const fb = _finiteLeffM(w.fillet_L_eff_m);
  const d1raw = _finiteLeffM(w.fillet_L_eff_m_diag1);
  const d2raw = _finiteLeffM(w.fillet_L_eff_m_diag2);
  const n1 = Number.isFinite(d1raw) ? d1raw : fb;
  const n2 = Number.isFinite(d2raw) ? d2raw : fb;
  const has1 = Number.isFinite(n1);
  const has2 = Number.isFinite(n2);
  if (!has1 && !has2) return '—';
  if (has1 && !has2) return n1.toFixed(3);
  if (!has1 && has2) return n2.toFixed(3);
  if (Math.abs(n1 - n2) < 5e-4) return n1.toFixed(3);
  return `d1: ${n1.toFixed(3)}, d2: ${n2.toFixed(3)}`;
}

function _finiteThroatMm(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 1e-4) return NaN;
  return n;
}

/** Throat a: single value when d1/d2/v match; otherwise d1 / d2 / v labels (ignore bogus zeros). */
function formatWeldThroatColumnMm(w) {
  const n1 = _finiteThroatMm(w.fillet_a_prov_mm_diag1);
  const n2 = _finiteThroatMm(w.fillet_a_prov_mm_diag2);
  const nv = w.fillet_a_prov_mm_vertical != null ? _finiteThroatMm(w.fillet_a_prov_mm_vertical) : NaN;
  const nFallback = _finiteThroatMm(w.fillet_a_prov_mm);
  const eps = 0.05;
  const has1 = Number.isFinite(n1);
  const has2 = Number.isFinite(n2);
  const hasV = Number.isFinite(nv);
  if (!has1 && !has2 && !hasV) {
    if (Number.isFinite(nFallback)) return displayThroatAMm({ ...w, fillet_a_prov_mm: nFallback });
    return { text: '—', clamped: false };
  }
  const same12 = has1 && has2 && Math.abs(n1 - n2) < eps;
  const sameAll = same12 && (!hasV || Math.abs(n1 - nv) < eps);
  if (sameAll && has1) return displayThroatAMm({ ...w, fillet_a_prov_mm: n1 });
  const parts = [];
  if (has1) parts.push(`d1: ${n1.toFixed(2)}`);
  if (has2 && (!has1 || Math.abs(n1 - n2) >= eps)) parts.push(`d2: ${n2.toFixed(2)}`);
  if (hasV && (!has1 || Math.abs(n1 - nv) >= eps)) parts.push(`v: ${nv.toFixed(2)}`);
  const minA = Math.min(
    ...(has1 ? [n1] : []),
    ...(has2 ? [n2] : []),
    ...(hasV ? [nv] : []),
  );
  const clamped = Number.isFinite(minA) && minA < FILLET_STRUCT_MIN_MM - 1e-9;
  return { text: parts.length ? parts.join(', ') : '—', clamped };
}

function visibleFilletWarnings(w, throatClamped) {
  const arr = Array.isArray(w.fillet_a_warnings) ? w.fillet_a_warnings : [];
  if (!throatClamped) return arr;
  return arr.filter(
    (s) =>
      typeof s !== 'string' ||
      (!s.includes('Throat a_prov') && !s.includes('structural shop minimum')),
  );
}

/** Bottom-chord anchor id for matplotlib K sketch; null if no evaluable row covers this weld row. */
function resolveJointPlotAnchorId(w, allWeldNotes) {
  if (!w || w.joint_node_id == null) return null;
  const nid = Number(w.joint_node_id);
  const evaluableRow = (u) =>
    u &&
    u.evaluable &&
    !u.central_placeholder &&
    u.joint_node_id != null &&
    Array.isArray(u.brace_other_nodes) &&
    u.brace_other_nodes.length >= 2 &&
    String(u.joint_mode || '') === 'node_joint';
  if (evaluableRow(w)) return nid;
  const hit = (allWeldNotes || []).find(
    (u) =>
      evaluableRow(u) &&
      (Number(u.joint_node_id) === nid || (u.brace_other_nodes || []).some((t) => Number(t) === nid)),
  );
  return hit != null ? Number(hit.joint_node_id) : null;
}

/** EC3 joint check row: capacity is reported at the chord node, not the diagonal member key. */
function jointCheckNodeLabel(c) {
  if (c.chord_end_node != null && Number.isFinite(Number(c.chord_end_node))) return String(c.chord_end_node);
  const mk = c.member_key || '';
  const m = mk.match(/_joint_(\d+)$/);
  return m ? m[1] : mk || '—';
}

/** One row per chord node: max utilization across diagonals meeting that node (same overlap branch in backend). */
function aggregateJointChecksByChordNode(jointChecks) {
  const byNode = new Map();
  for (const c of jointChecks) {
    const nodeKey = jointCheckNodeLabel(c);
    const u = Number(c.utilization);
    if (!Number.isFinite(u)) continue;
    const prev = byNode.get(nodeKey);
    if (!prev || u > Number(prev.utilization || 0)) {
      byNode.set(nodeKey, c);
    }
  }
  return [...byNode.entries()]
    .sort((a, b) => {
      const na = Number(a[0]);
      const nb = Number(b[0]);
      if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
      return String(a[0]).localeCompare(String(b[0]), undefined, { numeric: true });
    })
    .map(([, c]) => c);
}

/**
 * Collapse many chord segments into one row per chord line (max UR + governing key).
 * Dual-pitch keeps separate top_chord_left / top_chord_right; flat/mono use a single top_chord.
 */
function aggregateMemberChecksForDisplay(memberChecks) {
  const hasSplitTop = memberChecks.some((c) => c.group === 'top_chord_left' || c.group === 'top_chord_right');
  const byBucket = new Map();

  const bucketFor = (c) => {
    const g = c.group || '';
    if (g === 'bottom_chord') return 'bottom_chord';
    if (g === 'top_chord_left') return 'top_chord_left';
    if (g === 'top_chord_right') return 'top_chord_right';
    if (g === 'top_chord') return 'top_chord';
    return null;
  };

  const labelFor = (bucket) => {
    if (bucket === 'bottom_chord') return 'bottom_chord';
    if (bucket === 'top_chord_left') return 'top_chord_left';
    if (bucket === 'top_chord_right') return 'top_chord_right';
    return 'top_chord';
  };

  for (const c of memberChecks) {
    const bucket = bucketFor(c);
    if (!bucket) {
      const key = `other:${c.group || ''}:${c.member_key || ''}:${c.mode || ''}`;
      const u = Number(c.utilization);
      const prev = byBucket.get(key);
      if (!prev || (Number.isFinite(u) && u > Number(prev.utilization || 0))) {
        byBucket.set(key, { ...c, _aggBucket: null });
      }
      continue;
    }
    if (hasSplitTop && c.group === 'top_chord') continue;
    const u = Number(c.utilization);
    if (!Number.isFinite(u)) continue;
    const prev = byBucket.get(bucket);
    if (!prev || u > Number(prev.utilization || 0)) {
      byBucket.set(bucket, {
        ...c,
        group: labelFor(bucket),
        _aggBucket: bucket,
        _governing_member_key: c.member_key,
      });
    }
  }

  const chordOrder = ['top_chord', 'top_chord_left', 'top_chord_right', 'bottom_chord'];
  const chords = [];
  for (const b of chordOrder) {
    const row = byBucket.get(b);
    if (row) {
      chords.push({
        ...row,
        member_key: row._governing_member_key || row.member_key,
      });
      byBucket.delete(b);
    }
  }
  const others = [...byBucket.values()]
    .filter((r) => r._aggBucket == null)
    .sort((a, b) => {
      const ua = Number(a.utilization);
      const ub = Number(b.utilization);
      if (Number.isFinite(ua) && Number.isFinite(ub) && ua !== ub) return ub - ua;
      const ga = String(a.group || '');
      const gb = String(b.group || '');
      if (ga !== gb) return ga.localeCompare(gb);
      return String(a.member_key || '').localeCompare(String(b.member_key || ''), undefined, { numeric: true });
    });
  return [...chords, ...others];
}

/** Floating K-joint matplotlib preview (absolute overlay or inline sibling). */
function JointDebugFigurePanel({
  figureNodeId,
  plotCacheKey,
  jointDebugPlots,
  onClose,
  variant = 'overlay',
  loading = false,
}) {
  const cacheKey =
    plotCacheKey != null ? String(plotCacheKey) : figureNodeId != null ? String(figureNodeId) : null;
  const entry = cacheKey != null && jointDebugPlots?.by_node_id?.[cacheKey];
  const b64 = entry?.plot_b64;
  const hasB64 = typeof b64 === 'string' && b64.length > 32;
  if (figureNodeId == null) return null;
  if (!hasB64 && !loading) return null;
  const wrapSx =
    variant === 'overlay'
      ? (theme) => ({
          // Viewport-centered so opening from the weld table (below EC3) stays in view
          position: 'fixed',
          right: { xs: 8, sm: 16 },
          top: '50%',
          transform: 'translateY(-50%)',
          width: { xs: 'min(calc(100vw - 16px), 100%)', sm: 'min(44vw, 480px)' },
          maxHeight: 'min(85vh, 900px)',
          overflow: 'auto',
          zIndex: theme.zIndex.modal - 1,
        })
      : {
          flex: '0 0 min(100%, 400px)',
          maxWidth: '100%',
          maxHeight: '78vh',
          overflow: 'auto',
          position: 'relative',
        };
  return (
    <Paper
      elevation={12}
      sx={[
        wrapSx,
        {
          p: 1.5,
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
        },
      ]}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ pr: 1 }}>
          K-joint node {figureNodeId}
          {hasB64 && entry
            ? ` — ${entry.gen_time_ms} ms · total (pre) ${jointDebugPlots?.total_time_ms ?? '—'} ms`
            : loading
              ? ' — loading sketch…'
              : ''}
        </Typography>
        <IconButton size="small" aria-label="Close joint figure" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      {loading && !hasB64 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5 }}>
          <CircularProgress size={40} />
        </Box>
      ) : (
        <Box
          component="img"
          alt={`K-joint debug node ${figureNodeId}`}
          src={`data:image/png;base64,${b64}`}
          sx={{ width: '100%', height: 'auto', display: 'block', mt: 0.5 }}
        />
      )}
      {variant === 'overlay' && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          Anchored to the right at the vertical center of the window. The K-joint table stays on the left for the next
          row; EC3 Mode / Util. remain visible when you scroll up.
        </Typography>
      )}
    </Paper>
  );
}

/** Weld / K-joint table: one row per bottom-chord K joint (weld note). */
function WeldKJointTableRows({
  weldNotes,
  jointDebugNodeId,
  weldTableFocusNodeId,
  onPickWeldRow,
}) {
  const hsRow = (weldNotes || []).find((w) => w && typeof w.overlap_hidden_seam_welded === 'boolean');
  const hiddenSeamWelded = hsRow ? !!hsRow.overlap_hidden_seam_welded : false;
  const leffColTitle = [
    'Per diagonal: gap joints use chord-face L_eff (b_i,eff, h/sinθ, −2a).',
    'Overlap: left = under (λ_ov shortens side runs + b_eff; heel on chord only if hidden seam welded),',
    'right = through (full side runs + b_eff + strip on under face).',
    hiddenSeamWelded
      ? 'This run: hidden seam welded → longer under-brace L_eff (d1).'
      : 'This run: hidden seam unwelded (default) → under-brace L_eff excludes heel.',
    'Summary column uses min(d1,d2).',
  ].join(' ');
  const rows = [];
  weldNotes.forEach((w, i) => {
    rows.push({
      key: `wk-${i}`,
      w,
      displayNodeId: w.joint_node_id,
    });
  });

  const rowSelected = (r) => {
    const d = r.displayNodeId;
    if (d == null) return false;
    if (weldTableFocusNodeId != null) return Number(weldTableFocusNodeId) === Number(d);
    return Number(jointDebugNodeId) === Number(d);
  };

  return (
    <>
      <TableHead>
        <TableRow>
          <TableCell>Node</TableCell>
          <TableCell align="right">Brace 1 θ (°)</TableCell>
          <TableCell align="right">Brace 2 θ (°)</TableCell>
          <TableCell>Chord face gap / overlap</TableCell>
          <TableCell align="right" title={leffColTitle}>
            L_eff (m)
          </TableCell>
          <TableCell align="right">Throat a (mm)</TableCell>
          <TableCell>Warnings</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((r) => {
          const { w } = r;
          const { text: throatText, clamped: throatClamped } = formatWeldThroatColumnMm(w);
          const warnShown = visibleFilletWarnings(w, throatClamped);
          return (
            <TableRow
              key={r.key}
              hover
              selected={rowSelected(r)}
              onClick={() => {
                if (r.displayNodeId == null || !onPickWeldRow) return;
                onPickWeldRow(w);
              }}
              onDoubleClick={() => {
                if (r.displayNodeId == null || !onPickWeldRow) return;
                onPickWeldRow(w);
              }}
              sx={{
                cursor:
                  onPickWeldRow && resolveJointPlotAnchorId(w, weldNotes) != null ? 'pointer' : 'default',
              }}
            >
              <TableCell>
                {r.displayNodeId != null ? r.displayNodeId : '—'}
                {w.chord_leg === 'top' ? (
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                    (top chord)
                  </Typography>
                ) : null}
              </TableCell>
              <TableCell align="right">
                {Array.isArray(w.theta_brace_deg) && w.theta_brace_deg.length >= 1
                  ? Number(w.theta_brace_deg[0]).toFixed(1)
                  : '—'}
              </TableCell>
              <TableCell align="right">
                {Array.isArray(w.theta_brace_deg) && w.theta_brace_deg.length >= 2
                  ? Number(w.theta_brace_deg[1]).toFixed(1)
                  : '—'}
              </TableCell>
              <TableCell>{formatWeldChordGapMm(w)}</TableCell>
              <TableCell align="right">{formatWeldLeffColumnM(w)}</TableCell>
              <TableCell align="right">{throatText}</TableCell>
              <TableCell>
                {warnShown.length > 0 ? (
                  <Alert severity="warning" sx={{ py: 0, px: 0.5 }}>
                    {warnShown.join(' ')}
                  </Alert>
                ) : w.central_placeholder ? (
                  <Typography variant="caption" color="text.secondary">
                    Centre placeholder
                  </Typography>
                ) : (
                  '—'
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </>
  );
}

export default function TrussResults({ results, resultsOutOfDate, geometry, inputs }) {
  const [tab, setTab] = useState(0);
  const [jointDebugNodeId, setJointDebugNodeId] = useState(null);
  const [jointFigureFocusNodeId, setJointFigureFocusNodeId] = useState(null);
  const [weldTableFocusNodeId, setWeldTableFocusNodeId] = useState(null);
  const [jointPlotExtraByNode, setJointPlotExtraByNode] = useState({});
  const [jointPlotLoading, setJointPlotLoading] = useState(false);

  const weldNotesList = results?.weld_notes || [];
  const mergedJointDebugPlots = useMemo(() => {
    const base = results?.joint_debug_plots;
    if (!base) return null;
    return {
      ...base,
      by_node_id: { ...(base.by_node_id || {}), ...jointPlotExtraByNode },
    };
  }, [results?.joint_debug_plots, jointPlotExtraByNode]);

  const jointPlotCacheKey = useMemo(() => {
    if (jointDebugNodeId == null) return null;
    if (jointFigureFocusNodeId == null) return String(jointDebugNodeId);
    if (Number(jointFigureFocusNodeId) === Number(jointDebugNodeId)) return String(jointDebugNodeId);
    return `${jointDebugNodeId}:${jointFigureFocusNodeId}`;
  }, [jointDebugNodeId, jointFigureFocusNodeId]);

  useEffect(() => {
    setJointPlotExtraByNode({});
    setWeldTableFocusNodeId(null);
    setJointFigureFocusNodeId(null);
  }, [results]);

  const canFetchJointPlot = useMemo(() => {
    if (jointDebugNodeId == null || !results?.joint_debug_plots || results.joint_debug_plots.disabled) {
      return false;
    }
    return weldNotesList.some(
      (w) =>
        w.evaluable &&
        !w.central_placeholder &&
        String(w.joint_mode || '') === 'node_joint' &&
        w.joint_node_id != null &&
        Number(w.joint_node_id) === Number(jointDebugNodeId) &&
        Array.isArray(w.brace_other_nodes) &&
        w.brace_other_nodes.length >= 2,
    );
  }, [jointDebugNodeId, results?.joint_debug_plots, weldNotesList]);

  useEffect(() => {
    const jdp = results?.joint_debug_plots;
    const geomLocal = results?.geometry || geometry;
    if (!results || !jdp || jdp.disabled || jointDebugNodeId == null || !canFetchJointPlot) {
      setJointPlotLoading(false);
      return undefined;
    }
    const nodes = geomLocal?.nodes;
    const members = geomLocal?.members;
    const weld_notes = results.weld_notes || geomLocal?.weld_notes || [];
    if (!nodes?.length || !members?.length) {
      setJointPlotLoading(false);
      return undefined;
    }

    const key = jointPlotCacheKey != null ? String(jointPlotCacheKey) : String(jointDebugNodeId);
    const fromBase = jdp.by_node_id?.[key]?.plot_b64;
    const fromExtra = jointPlotExtraByNode[key]?.plot_b64;
    const cached =
      (typeof fromBase === 'string' && fromBase.length > 32) ||
      (typeof fromExtra === 'string' && fromExtra.length > 32);
    if (cached) {
      setJointPlotLoading(false);
      return undefined;
    }

    const row = weldNotesList.find(
      (w) =>
        w.joint_node_id != null &&
        Number(w.joint_node_id) === Number(jointDebugNodeId) &&
        w.evaluable &&
        !w.central_placeholder &&
        String(w.joint_mode || '') === 'node_joint' &&
        Array.isArray(w.brace_other_nodes) &&
        w.brace_other_nodes.length >= 2,
    );

    const ac = new AbortController();
    let cancelled = false;
    setJointPlotLoading(true);
    axios
      .post(
        '/api/truss-analysis/calculations/joint-debug-plot/',
        {
          joint_node_id: jointDebugNodeId,
          view_node_id:
            jointFigureFocusNodeId != null &&
            Number(jointFigureFocusNodeId) !== Number(jointDebugNodeId)
              ? jointFigureFocusNodeId
              : undefined,
          nodes,
          members,
          weld_notes,
        },
        { signal: ac.signal },
      )
      .then((res) => {
        if (cancelled) return;
        const d = res.data;
        if (d?.plot_b64) {
          setJointPlotExtraByNode((prev) => ({
            ...prev,
            [key]: {
              plot_b64: d.plot_b64,
              gen_time_ms: d.gen_time_ms,
              panel_index: d.panel_index ?? row?.panel_index,
            },
          }));
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setJointPlotLoading(false);
      });
    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [
    jointDebugNodeId,
    jointFigureFocusNodeId,
    jointPlotCacheKey,
    results,
    geometry,
    canFetchJointPlot,
    weldNotesList,
    jointPlotExtraByNode,
  ]);

  const jointMarkIds = useMemo(
    () =>
      weldNotesList
        .filter(
          (w) =>
            w.evaluable &&
            String(w.joint_mode || '') === 'node_joint' &&
            Array.isArray(w.brace_other_nodes) &&
            w.brace_other_nodes.length >= 2 &&
            w.joint_node_id != null,
        )
        .map((w) => Number(w.joint_node_id)),
    [weldNotesList],
  );

  const topJointMarkIds = useMemo(() => {
    const s = new Set();
    weldNotesList.forEach((w) => {
      if (!w.evaluable || w.central_placeholder || !Array.isArray(w.brace_other_nodes)) return;
      w.brace_other_nodes.forEach((tid) => {
        if (tid != null) s.add(Number(tid));
      });
    });
    return [...s];
  }, [weldNotesList]);

  const geomForBraceLabels = results?.geometry || geometry;
  const braceEndpointNodeIds = useMemo(() => {
    const s = new Set();
    (geomForBraceLabels?.members || []).forEach((m) => {
      if (m.group === 'diagonal' || m.group === 'vertical') {
        if (m.i != null) s.add(Number(m.i));
        if (m.j != null) s.add(Number(m.j));
      }
    });
    return [...s];
  }, [geomForBraceLabels?.members]);

  const handlePickWeldRow = useCallback(
    (w) => {
      if (w == null || w.joint_node_id == null) return;
      const anchor = resolveJointPlotAnchorId(w, weldNotesList);
      if (anchor == null) return;
      setWeldTableFocusNodeId(Number(w.joint_node_id));
      setJointDebugNodeId(anchor);
      setJointFigureFocusNodeId(Number(w.joint_node_id));
    },
    [weldNotesList],
  );

  useEffect(() => {
    if (!jointMarkIds.length) {
      setJointDebugNodeId(null);
      setWeldTableFocusNodeId(null);
      setJointFigureFocusNodeId(null);
      return;
    }
    setJointDebugNodeId((prev) => {
      if (prev != null && jointMarkIds.includes(prev)) return prev;
      const first = jointMarkIds[0];
      setJointFigureFocusNodeId(first);
      setWeldTableFocusNodeId(first);
      return first;
    });
  }, [jointMarkIds]);

  if (!results) return null;
  if (results.error) {
    return (
      <Paper sx={{ p: 2, mt: 2 }}>
        <Alert severity="error">{results.error}</Alert>
      </Paper>
    );
  }

  const sol = results.solution || {};
  const checks = results.design_checks || [];
  const chordFailGroups = new Set([
    'top_chord',
    'bottom_chord',
    'top_chord_left',
    'top_chord_right',
    'column_outer',
    'column_inner',
    'arch_rib',
    'arch_leg',
    'column',
  ]);
  const chordFailures = checks
    .filter((c) => chordFailGroups.has(c.group) && Number(c.utilization) > 1)
    .sort((a, b) => Number(b.utilization) - Number(a.utilization));
  const designRejected = chordFailures.length > 0 || results.arch_design?.accepted === false;
  const showDesignFigure = !designRejected;
  const defl = results.deflection || {};
  const combo = results.loads_combo || {};
  const governingText = formatGoverningCombination(results, inputs);
  const memberChecksRaw = checks.filter((c) => c.group !== 'joint');
  const memberChecks = aggregateMemberChecksForDisplay(memberChecksRaw);
  const jointChecks = checks.filter((c) => c.group === 'joint');
  const jointChecksTable = aggregateJointChecksByChordNode(jointChecks);
  const showJointLoadVariantCol = jointChecks.some((c) => c.governing_load_variant);
  const showChordMomentCol = memberChecks.some(
    (c) => c.M_combined_kNm != null && Number.isFinite(Number(c.M_combined_kNm)),
  );
  const geom = results.geometry || geometry;
  const profileRows = profilesSummary(results.selected_profiles, results.geometry);
  const ci = results.chord_imperfection || results.fem_imperfection;

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Analysis results
        {resultsOutOfDate && (
          <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1 }}>
            (inputs changed — recalculate)
          </Typography>
        )}
      </Typography>

      {designRejected && (
        <Alert severity="error" sx={{ my: 1 }}>
          {chordFailures.length
            ? `Chords do not pass (${chordFailures[0].group} ${chordFailures[0].member_key || ''} at ${utilPercent(chordFailures[0].utilization)}). This is not a valid design, so the sized-member figure is withheld.`
            : results.arch_design?.note ||
              'No section met the arch checks together. This is not a valid design, so the sized-member figure is withheld.'}
        </Alert>
      )}

      {(governingText || combo.name) && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Governing combination: {governingText || combo.name}
        </Typography>
      )}

      {results.solver_note && (
        <Alert severity="info" sx={{ my: 1 }}>
          {results.solver_note}
        </Alert>
      )}

      {results.snow_checkerboard_ul?.enabled && (
        <Alert severity="info" sx={{ my: 1 }}>
          {results.snow_checkerboard_ul.note}{' '}
          <Typography component="span" variant="body2">
            Variants enveloped: {(results.snow_checkerboard_ul.variants_enveloped || []).join(', ')}.
          </Typography>
        </Alert>
      )}

      {results.snow_meta?.snow_sk_source === 'coordinates' && (
        <Alert severity="info" sx={{ my: 1 }}>
          Ground snow s_k from coordinates: {Number(results.snow_meta.sk_kN_m2 ?? results.sk_kN_m2 ?? 0).toFixed(3)} kN/m²
          {results.snow_meta.method ? ` (${results.snow_meta.method})` : ''}
        </Alert>
      )}

      {results.arch_design?.chord_name && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Arch rib: {results.arch_design.chord_name}
          {results.arch_design.sized_by ? ` — ${results.arch_design.sized_by}` : ''}
        </Typography>
      )}

      {results.auto_design && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Auto design: panels = {results.auto_design.selected_panel_count}
          {results.auto_design.open_chord_shape != null
            ? ` · open profile ${results.auto_design.open_chord_shape}`
            : ''}
          {results.auto_design.estimated_steel_kg != null
            ? ` · ≈ ${Number(results.auto_design.estimated_steel_kg).toFixed(0)} kg steel (members only)`
            : ''}
          {results.auto_design.optimization_cost_eur && (
            <>
              {' '}
              · objective ≈ {Number(results.auto_design.optimization_cost_eur.total_objective_eur).toFixed(0)} EUR
              (steel {Number(results.auto_design.optimization_cost_eur.steel_cost_eur).toFixed(0)} + overlap{' '}
              {Number(results.auto_design.optimization_cost_eur.overlap_cost_eur).toFixed(0)}
              {results.auto_design.optimization_cost_eur.overlap_bottom_joint_count != null
                ? `, ${results.auto_design.optimization_cost_eur.overlap_bottom_joint_count} overlap joints`
                : ''}
              )
            </>
          )}
        </Typography>
      )}

      {results.column_auto_design?.selected_column && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Auto column:{' '}
            {results.column_auto_design.selected_column.name || results.column_auto_design.selected_column.type}
            {results.column_auto_design.k_factor != null
              ? `  K = ${Number(results.column_auto_design.k_factor).toFixed(2)}`
              : ''}
          </Typography>
          {results.column_auto_design.deflection_note && (
            <Alert severity="warning" sx={{ mt: 0.5 }}>
              {results.column_auto_design.deflection_note}
            </Alert>
          )}
          {results.column_auto_design.column_weak_axis_brace_proposed_m != null && (
            <Typography variant="caption" color="text.secondary" display="block">
              Suggested weak-axis brace interval:{' '}
              {Number(results.column_auto_design.column_weak_axis_brace_proposed_m).toFixed(2)} m
              {results.column_auto_design.weak_axis_non_governing ? ' (keeps strong-axis behavior governing)' : ''}
            </Typography>
          )}
        </Box>
      )}

      {results.geometry?.support_on_top && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Supports on top chord
          {results.geometry.bearing_connection_offset_m > 0
            ? ` · offset ${Number(results.geometry.bearing_connection_offset_m).toFixed(2)} m (end top-chord N+M check uses reaction × offset; mesh uses equal bays in the clear span)`
            : ''}
        </Typography>
      )}
      {results.geometry?.bearing_connection_offset_m > 0 &&
        results.geometry?.internal_panel_length_m != null && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Web panels: equal length {Number(results.geometry.internal_panel_length_m).toFixed(3)} m between inset chord
            stations (stub segments at bearings have chord only).
          </Typography>
        )}

      {results.chord_bracing && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Chord buckling lengths: in-plane {Number(results.chord_bracing.L_plane_m || 0).toFixed(3)} m, lateral{' '}
          {Number(results.chord_bracing.L_lateral_m || 0).toFixed(3)} m (panel / web spacing{' '}
          {Number(results.chord_bracing.L_panel_m || 0).toFixed(3)} m
          {results.chord_bracing.L_mesh_segment_m != null
            ? `; mesh segment ${Number(results.chord_bracing.L_mesh_segment_m).toFixed(3)} m`
            : ''}
          ).
          {results.chord_bracing.notation_note ? ` ${results.chord_bracing.notation_note}` : ''}
        </Typography>
      )}

      <Card variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
        <CardHeader title="Result sections" titleTypographyProps={{ variant: 'overline', letterSpacing: 1.2 }} sx={{ pb: 0 }} />
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
            <Tab label="Summary" />
            <Tab label="Member checks" />
            <Tab label="Joints" />
            <Tab label="Forces" />
            <Tab label="Reactions" />
            <Tab label="Deflection" />
            <Tab label="Loadings" />
            {results.building && <Tab label="Load paths" />}
          </Tabs>
        </Box>
      </Card>

      <TabPanel value={tab} index={0}>
        {(governingText || combo.name) && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Governing load combination
            </Typography>
            <Typography variant="body1">{governingText || combo.name}</Typography>
          </Box>
        )}
        <Typography variant="subtitle2" gutterBottom>
          Chosen profiles
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableBody>
              {profileRows.map((r) => (
                <TableRow key={r.label}>
                  <TableCell>{r.label}</TableCell>
                  <TableCell>{r.value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {ci && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, mb: 0.5 }}>
            {ci.included === false
              ? 'Chord imperfection: off'
              : `Chord imperfection: span / ${Number(ci.span_ratio || 500)} → ${Number(ci.amplitude_mid_mm || 0).toFixed(2)} mm at midspan (top + bottom chords).`}
          </Typography>
        )}
        {results.building && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Whole building
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Frames: {results.building.num_frames} · Length: {Number(results.building.building_length_m).toFixed(1)} m ·
              System: {results.building.stability_system}
              {results.cbf_mode ? ' (CBF)' : ''}
              {results.portal_mode ? ' (portal)' : ''}
              {results.frame_line_envelope?.governing_frame_line_index != null
                ? ` · Design frame line ${results.frame_line_envelope.governing_frame_line_index}`
                : ''}
            </Typography>
            {results.building_stability && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Stability governing: {results.building_stability.governing_direction} · λ_cr ={' '}
                {results.building_stability.governing_lambda_cr != null
                  ? Number(results.building_stability.governing_lambda_cr).toFixed(3)
                  : '—'}
              </Typography>
            )}
            {results.longitudinal_bracing?.longitudinal_drift_mm != null && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Longitudinal drift (roof bracing): {Number(results.longitudinal_bracing.longitudinal_drift_mm).toFixed(2)} mm
              </Typography>
            )}
            {results.bracing_needs && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Bracing layout ({results.bracing_needs.layout_mode}):{' '}
                {results.bracing_needs.num_bracing_links} links
                {results.bracing_needs.stabilizing_actions?.delta_q_over_e0 != null &&
                  ` · δq/e₀ = ${Number(results.bracing_needs.stabilizing_actions.delta_q_over_e0).toFixed(3)} (${results.bracing_needs.stabilizing_actions.stiffness_gate})`}
              </Typography>
            )}
            {results.bracing_adequacy && Object.keys(results.bracing_adequacy).length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Diaphragm adequacy: {results.bracing_adequacy.pass ? 'pass' : 'fail'} · mode:{' '}
                  {results.bracing_adequacy.governing_mode || '—'}
                  {results.bracing_adequacy.explicit_bracing_required != null &&
                    ` · X-bracing required: ${results.bracing_adequacy.explicit_bracing_required ? 'yes' : 'no'}`}
                </Typography>
                {results.bracing_adequacy.stiffness?.c_panel_mm_per_kn != null && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    c = {Number(results.bracing_adequacy.stiffness.c_panel_mm_per_kn).toFixed(3)} mm/kN
                    {results.bracing_adequacy.stiffness.s_kn != null &&
                      ` · S = ${Number(results.bracing_adequacy.stiffness.s_kn).toFixed(1)} kN`}
                    {' · credit: '}
                    {results.bracing_adequacy.stiffness.credit_level}
                    {results.bracing_adequacy.stiffness.metadata?.eccs88_assumptions?.fixing &&
                      ` · fixing: ${results.bracing_adequacy.stiffness.metadata.eccs88_assumptions.fixing.replace('_', ' ')}`}
                    {results.bracing_adequacy.stiffness.metadata?.eccs88_assumptions?.K1 != null &&
                      ` · K1=${Number(results.bracing_adequacy.stiffness.metadata.eccs88_assumptions.K1).toFixed(3)}`}
                  </Typography>
                )}
                {(results.bracing_adequacy.stiffness?.warnings || []).map((w) => (
                  <Typography key={w} variant="caption" color="text.secondary" display="block">
                    {w}
                  </Typography>
                ))}
                {(results.bracing_adequacy.eligibility_errors || []).map((e) => (
                  <Typography key={e} variant="caption" color="error.main" display="block">
                    {e}
                  </Typography>
                ))}
              </Box>
            )}
            {results.sn027_bottom_chord?.performed && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  SN027a bottom chord: {results.sn027_bottom_chord.pass ? 'pass' : 'fail'} · l₀ ={' '}
                  {Number(results.sn027_bottom_chord.l0_m || 0).toFixed(2)} m · util{' '}
                  {Number(results.sn027_bottom_chord.utilization_sn027 || 0).toFixed(2)} (baseline{' '}
                  {Number(results.sn027_bottom_chord.utilization_baseline || 0).toFixed(2)})
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  ks = {Number(results.sn027_bottom_chord.stiffness?.ks_kN_per_m2 || 0).toFixed(1)} kN/m²
                  (k₁={Number(results.sn027_bottom_chord.stiffness?.k1_kN_per_m2 || 0).toFixed(1)},
                  k₂={Number(results.sn027_bottom_chord.stiffness?.k2_kN_per_m2 || 0).toFixed(1)},
                  k₃={Number(results.sn027_bottom_chord.stiffness?.k3_kN_per_m2 || 0).toFixed(1)})
                </Typography>
              </Box>
            )}
            {results.sn027_bottom_chord?.applicable === false && results.sn027_bottom_chord?.reason && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                SN027a: {results.sn027_bottom_chord.reason}
              </Typography>
            )}
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Stressed-skin top chord L_lat = {Number(results.member_restraint.top_chord_lateral_m || 0).toFixed(2)} m
                (bottom chord unchanged unless explicitly attached)
              </Typography>
            {results.code_generation === 2 && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Second-generation Eurocode with recommended parameters, for prescreening, until the national annex in force is applied.
                {results.alpha_cr != null ? ` α_cr = ${Number(results.alpha_cr).toFixed(2)}.` : ''}{' '}
                Analysis method {results.analysis_method || 'M1'}
                {results.second_order_p_delta ? ', frame P-Δ on.' : '.'} Governing frame.
              </Typography>
            )}
            {(results.analysis_warnings || []).map((w) => (
              <Typography key={w} variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                {w}
              </Typography>
            ))}
            {(results.building_warnings || []).map((w) => (
              <Typography key={w} variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                {w}
              </Typography>
            ))}
            {results.experimental_notice && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {results.experimental_notice}
              </Typography>
            )}
            {results.foundation_summary?.uplift_warning && (
              <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.5 }}>
                Foundation uplift detected: {Number(results.foundation_summary.max_uplift_kN).toFixed(2)} kN
              </Typography>
            )}
          </Box>
        )}
        {results.portal_sway_imperfection && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Portal sway (ULS):{' '}
            {results.portal_sway_imperfection.applied
              ? `φ = ${Number(results.portal_sway_imperfection.phi || 0).toFixed(5)}, H = ${(Number(results.portal_sway_imperfection.H_N || 0) / 1000).toFixed(2)} kN (V_Ed = ${(Number(results.portal_sway_imperfection.V_ed_N || 0) / 1000).toFixed(2)} kN)`
              : results.portal_sway_imperfection.note || 'Not applied'}
          </Typography>
        )}
        {results.portal_knee && (
          <Box sx={{ mt: 1 }}>
            {defl.drift_H_mm != null && Number(defl.drift_H_mm) !== 0 && (
              <Typography variant="body2" color="text.secondary">
                Portal SLS lateral drift (char. wind + G; used for h / drift limit): |u|_max ={' '}
                {Number(defl.drift_H_mm || 0).toFixed(2)} mm at eaves; d/h ={' '}
                {(Number(defl.drift_ratio) || 0).toExponential(2)} (limit 1/
                {Number(defl.drift_limit_ratio || 150).toFixed(0)})
                {defl.drift_ok === false ? ' — exceeds limit' : ''}
              </Typography>
            )}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: defl.drift_H_mm != null && Number(defl.drift_H_mm) !== 0 ? 0.5 : 0 }}
            >
              Eave u_x (ULS envelope — max |u_x| over all combinations): left{' '}
              {Number(results.portal_knee?.eave_lateral_displacement_mm?.left || 0).toFixed(2)} mm
              {results.portal_knee?.eave_lateral_displacement_uls_variant?.left
                ? ` (${String(results.portal_knee.eave_lateral_displacement_uls_variant.left)})`
                : ''}
              , right {Number(results.portal_knee?.eave_lateral_displacement_mm?.right || 0).toFixed(2)} mm
              {results.portal_knee?.eave_lateral_displacement_uls_variant?.right
                ? ` (${String(results.portal_knee.eave_lateral_displacement_uls_variant.right)})`
                : ''}
              .
            </Typography>
          </Box>
        )}
        {results.joint_envelope && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            <strong>Governing joint (ULS envelope)</strong>: node {jointCheckNodeLabel(results.joint_envelope)} —{' '}
            {results.joint_envelope.mode || '—'}
            {results.joint_envelope.governing_load_variant
              ? ` (${results.joint_envelope.governing_load_variant})`
              : ''}
            {', '}
            util. {utilPercent(results.joint_envelope.utilization)}
            {results.joint_envelope.N_Rd_kN != null && Number.isFinite(Number(results.joint_envelope.N_Rd_kN))
              ? `, N_Rd = ${Number(results.joint_envelope.N_Rd_kN).toFixed(1)} kN`
              : ''}
            .
          </Typography>
        )}
        <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>
          Deflection &amp; precamber (SLS characteristic)
        </Typography>
        <Typography variant="body2">
          Max bottom-chord vertical displacement (uz): {Number(defl.uz_max_mm || defl.uy_max_mm || 0).toFixed(2)} mm (limit{' '}
          {Number(defl.limit_mm || 0).toFixed(1)} mm)
        </Typography>
        {results.chord_imperfection?.included ? (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Imperfection bow: {Number(results.chord_imperfection.amplitude_mid_mm || 0).toFixed(2)} mm
          </Typography>
        ) : null}
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          <strong>Suggested precamber</strong> (fabrication): {Number(defl.suggested_precamber_mm || 0).toFixed(2)} mm
        </Typography>
        {!defl.ok && <Alert severity="warning">Deflection exceeds serviceability limit.</Alert>}
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Chord lines show the governing segment only (max utilization). Dual-pitch keeps separate left/right top
          chords; flat/mono use one top chord group. Other bars are listed one by one, highest utilization first.
        </Typography>
        {showDesignFigure && geom && sol && (
          <TrussResultsFigure
            geometry={geom}
            solution={sol}
            designChecks={checks}
            showMemberKeys
            containerWidthPercent={95}
            title="Member keys on geometry"
          />
        )}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Group</TableCell>
                <TableCell>Key</TableCell>
                <TableCell>Section</TableCell>
                <TableCell>Mode</TableCell>
                {showChordMomentCol && (
                  <TableCell align="right" title="Chord ULS moment: continuous chord FE + bearing offset / weld eccentricity">
                    M (kN·m)
                  </TableCell>
                )}
                <TableCell align="right">Util.</TableCell>
                <TableCell width="28%"> </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {memberChecks.map((c, i) => (
                <TableRow key={i}>
                  <TableCell>{c.group}</TableCell>
                  <TableCell>{c.member_key}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{c.section_display || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" component="span">
                      {c.mode || '—'}
                    </Typography>
                    {c.governing_load_variant ? (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                        {formatLoadVariantLabel(c.governing_load_variant)}
                      </Typography>
                    ) : null}
                  </TableCell>
                  {showChordMomentCol && (
                    <TableCell align="right">
                      {c.M_combined_kNm != null && Number.isFinite(Number(c.M_combined_kNm)) ? (
                        <span>
                          {Number(c.M_combined_kNm).toFixed(2)}
                          <Typography component="span" variant="caption" color="text.secondary" display="block">
                            FE {Number(c.M_fem_kNm || 0).toFixed(2)} + conn {Number(c.M_connection_kNm || 0).toFixed(2)}
                          </Typography>
                        </span>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  )}
                  <TableCell align="right">{utilPercent(c.utilization)}</TableCell>
                  <TableCell>{utilBar(Number(c.utilization))}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      <TabPanel value={tab} index={2}>
        {showDesignFigure && geom && sol && (
          <TrussResultsFigure
            geometry={geom}
            solution={sol}
            designChecks={checks}
            title="Geometry — joint markers"
            bottomJointMarkNodeIds={jointMarkIds}
            topJointMarkNodeIds={topJointMarkIds}
            braceEndpointNodeIds={braceEndpointNodeIds}
            selectedJointNodeId={jointDebugNodeId}
            onJointMarkerClick={(id) => {
              setJointDebugNodeId(id);
              setWeldTableFocusNodeId(id);
              setJointFigureFocusNodeId(id);
            }}
            onTopJointMarkerClick={(topId) => {
              const row = weldNotesList.find(
                (w) =>
                  w.evaluable &&
                  !w.central_placeholder &&
                  Array.isArray(w.brace_other_nodes) &&
                  w.brace_other_nodes.some((t) => Number(t) === Number(topId)),
              );
              if (row?.joint_node_id != null) {
                setJointDebugNodeId(Number(row.joint_node_id));
                setWeldTableFocusNodeId(Number(topId));
                setJointFigureFocusNodeId(Number(topId));
              }
            }}
            containerWidthPercent={95}
          />
        )}
        <Box sx={{ mt: 2 }}>
          {jointChecksTable.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: weldNotesList.length ? 1 : 0 }}>
              No joint checks in this model.
            </Typography>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                EN 1993-1-8 tubular checks are run per diagonal at the chord connection (RHS uses one gap vs overlap
                branch per bottom geometry). Rows below show the governing case per chord node (max utilization).
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Node</TableCell>
                      {showJointLoadVariantCol && <TableCell>ULS load</TableCell>}
                      <TableCell>Mode</TableCell>
                      <TableCell align="right">Util.</TableCell>
                      <TableCell width="40%"> </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {jointChecksTable.map((c, i) => (
                      <TableRow key={`${jointCheckNodeLabel(c)}-${i}`}>
                        <TableCell>{jointCheckNodeLabel(c)}</TableCell>
                        {showJointLoadVariantCol && (
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {c.governing_load_variant || '—'}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell>{c.mode}</TableCell>
                        <TableCell align="right">{utilPercent(c.utilization)}</TableCell>
                        <TableCell>{utilBar(Number(c.utilization))}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
          {weldNotesList.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mt: jointChecksTable.length ? 2 : 1, mb: 0.5 }}>
                K-joint / weld rows (click row to open sketch)
              </Typography>
              {jointChecksTable.length === 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: 'flex-start',
                    gap: 2,
                    position: 'relative',
                  }}
                >
                  <TableContainer sx={{ flex: 1, minWidth: 0, alignSelf: 'stretch' }}>
                    <Table size="small">
                      <WeldKJointTableRows
                        weldNotes={weldNotesList}
                        jointDebugNodeId={jointDebugNodeId}
                        weldTableFocusNodeId={weldTableFocusNodeId}
                        onPickWeldRow={handlePickWeldRow}
                      />
                    </Table>
                  </TableContainer>
                  <JointDebugFigurePanel
                    figureNodeId={jointFigureFocusNodeId ?? jointDebugNodeId}
                    plotCacheKey={jointPlotCacheKey ?? undefined}
                    jointDebugPlots={mergedJointDebugPlots ?? { disabled: true, by_node_id: {} }}
                    onClose={() => {
                      setJointDebugNodeId(null);
                      setWeldTableFocusNodeId(null);
                      setJointFigureFocusNodeId(null);
                    }}
                    variant="side"
                    loading={jointPlotLoading && canFetchJointPlot}
                  />
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <WeldKJointTableRows
                      weldNotes={weldNotesList}
                      jointDebugNodeId={jointDebugNodeId}
                      weldTableFocusNodeId={weldTableFocusNodeId}
                      onPickWeldRow={handlePickWeldRow}
                    />
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </Box>
        {jointChecksTable.length > 0 && (
          <JointDebugFigurePanel
            figureNodeId={jointFigureFocusNodeId ?? jointDebugNodeId}
            plotCacheKey={jointPlotCacheKey ?? undefined}
            jointDebugPlots={mergedJointDebugPlots ?? { disabled: true, by_node_id: {} }}
            onClose={() => {
              setJointDebugNodeId(null);
              setWeldTableFocusNodeId(null);
              setJointFigureFocusNodeId(null);
            }}
            variant="overlay"
            loading={jointPlotLoading && canFetchJointPlot}
          />
        )}
        {(results.weld_eccentricities || []).length > 0 && (
          <Box sx={{ mt: 2 }}>
            {(results.weld_eccentricities || []).map((w, i) => (
              <Typography key={`e-${i}`} variant="body2" sx={{ mb: 0.5 }}>
                Bottom node {w.bottom_node_id}: e = {(w.e_m * 1000).toFixed(1)} mm — included in chord M where
                applicable.
              </Typography>
            ))}
          </Box>
        )}
        {jointChecksTable.length === 0 &&
          weldNotesList.length === 0 &&
          !(results.weld_eccentricities || []).length && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              No weld gap or offset notes.
            </Typography>
          )}
      </TabPanel>

      <TabPanel value={tab} index={3}>
        <TrussForcesFigure geometry={geom} solution={sol} momentCases={results.moment_cases} />
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Chords are continuous beam elements; axial N is reported at the first node of each segment (full V, M at
          both ends are in the analysis payload frame_forces).
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>i</TableCell>
                <TableCell>j</TableCell>
                <TableCell>Group</TableCell>
                <TableCell align="right">N (tension +)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(sol.truss_forces || []).map((f, i) => (
                <TableRow key={`t-${i}`}>
                  <TableCell>{f.i}</TableCell>
                  <TableCell>{f.j}</TableCell>
                  <TableCell>{f.group}</TableCell>
                  <TableCell align="right">{Number(f.N_kN).toFixed(2)}</TableCell>
                </TableRow>
              ))}
              {(sol.frame_forces || [])
                .filter((f) =>
                  ['bottom_chord', 'top_chord', 'top_chord_left', 'top_chord_right', 'arch_rib', 'arch_leg', 'column_outer', 'column_inner', 'column'].includes(f.group),
                )
                .map((f, i) => (
                  <TableRow key={`f-${i}`}>
                    <TableCell>{f.i}</TableCell>
                    <TableCell>{f.j}</TableCell>
                    <TableCell>{f.group}</TableCell>
                    <TableCell align="right">{Number(f.N_i_kN).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      <TabPanel value={tab} index={4}>
        {Array.isArray(results.reaction_envelope) && results.reaction_envelope.length > 0 ? (
          <>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Algebraic minimum and maximum of each support reaction over every ULS combination. Hover a value for
              the combination that produced it.
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Node</TableCell>
                    <TableCell>DOF</TableCell>
                    <TableCell align="right">Min</TableCell>
                    <TableCell align="right">Max</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.reaction_envelope.map((r, i) => (
                    <TableRow key={`${r.node}-${r.dof}-${i}`}>
                      <TableCell>{r.node}</TableCell>
                      <TableCell>{r.dof}</TableCell>
                      <TableCell align="right" title={r.min_combo || ''}>
                        {Number(r.min).toFixed(2)}
                      </TableCell>
                      <TableCell align="right" title={r.max_combo || ''}>
                        {Number(r.max).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Node</TableCell>
                  <TableCell>DOF</TableCell>
                  <TableCell align="right">Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(sol.reactions || []).map((r, i) => (
                  <TableRow key={i}>
                    <TableCell>{r.node}</TableCell>
                    <TableCell>{r.dof}</TableCell>
                    <TableCell align="right">{Number(r.value).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      <TabPanel value={tab} index={5}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Max bottom-chord vertical displacement (uz): {Number(defl.uz_max_mm || defl.uy_max_mm || 0).toFixed(2)} mm (limit{' '}
          {Number(defl.limit_mm || 0).toFixed(1)} mm). Suggested precamber:{' '}
          {Number(defl.suggested_precamber_mm || 0).toFixed(2)} mm.
        </Typography>
        {defl.drift_H_mm != null && Number(defl.drift_H_mm) !== 0 && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            Portal SLS lateral drift: {Number(defl.drift_H_mm || 0).toFixed(2)} mm; δ/h = {(Number(defl.drift_ratio) || 0).toExponential(2)}{' '}
            (limit 1/{Number(defl.drift_limit_ratio || 150).toFixed(0)})
            {defl.drift_ok === false ? ' — exceeds limit' : ''}
          </Typography>
        )}
        {!defl.ok && <Alert severity="warning">Deflection exceeds limit.</Alert>}
        {showDesignFigure && geom && sol && (
          <TrussResultsFigure
            geometry={geom}
            solution={sol}
            designChecks={checks}
            showMemberForces={false}
            title="Nodal deflections & deflected shape"
          />
        )}
      </TabPanel>

      <TabPanel value={tab} index={6}>
        <TrussLoadingsView results={results} inputs={inputs} />
      </TabPanel>

      {results.building && (
        <TabPanel value={tab} index={7}>
          <BracingLoadPathsView results={results} />
        </TabPanel>
      )}
    </Paper>
  );
}
